import * as React from 'react';

import { Dispatch } from 'redux';
import { connect } from 'react-redux';
import isEqual from 'lodash/isEqual';
import memoize from 'lodash/memoize';

import { CallHistoryMethodAction, push } from 'connected-react-router';

import styled from 'styled-components';

import { AnimatePresence } from 'framer-motion';

import { Translate } from 'react-localize-redux';

// Store
import AppState from '@webapp/store/state';
import { trackList } from '@webapp/store/tracks/selectors';
import { blockEditor } from '@webapp/store/release/selectors';
import {
  DisableReplicateCollaborators,
  UpdateInvalid,
} from '@webapp/store/tracks/action-creators';
import { assetOwnersById } from '@webapp/store/ownership/selectors';
import { collaboratorsList } from '@webapp/store/collaborator/selectors';
import { GetCollaborators } from '@webapp/store/collaborator/action-creators';
import { GetOwnerships } from '@webapp/store/ownership/action-creators';
import {
  ArtistInviteSource,
  CreateArtistInvite,
} from '@webapp/store/artist/action-creators';
import { ShowSnackbar } from '@webapp/store/ui/action-creators';

// Types
import SimpleTrack from '@common/types/release/simple-track';
import SimpleArtist from '@common/types/artist/simple-artist';
import { ReleaseType } from '@common/types/release/release-type';
import { ReleaseStatus } from '@common/types/release/relase-status';

import { ContactInfo } from '@webapp/types/common/contact-info';

// Utils
import { isISRC } from '@webapp/utils/validators';
import scrollOnError from '@webapp/utils/release/scroll-on-error';
import {
  trackSection,
  fullRelease,
  validStatusForSummaryButton,
} from '@webapp/utils/validators/release';
import { COLLABORATOR_ROLES } from '@webapp/utils/release/constants';

// Services
import { NewCollaborator } from '@services/collaborator/types/new-collaborator';
import { InviteArtistRequest } from '@services/artist/types/invite-artist-request';
import { RecordLabelSubscriptionType } from '@services/subscription/types/record-label-subcription';

// Components
import MotionOpacity from '@components/motion/motion-opacity';
import CounterFixedButton from '@components/actions/counter-fixed-button';
import SummaryButton from '@components/actions/summary-button';

// Controllers
import TrackPreviewCtrl from '@webapp/controllers/track/preview';
import TrackLoadingCtrl from '@webapp/controllers/track/track-loading';
import CollaboratorsCtrl from '@webapp/controllers/track/collaborators';
import TrackInfoTitleCtrl from '@webapp/controllers/release/track-info-title';
import CoverCopyrightMaterialCtrl from '@webapp/controllers/release/cover-copyright';
import { UpdateCollaboratorsPlatforms } from '@webapp/controllers/track/collaborators/collaborator-list';
import GeneralTrackInfo from '@webapp/pages/release/release-track-info/general-track-info';
import { CreditsTrackInfo } from '@webapp/pages/release/release-track-info/credits-track-info';
import WriterOwnership from '@webapp/pages/release/release-track-info/writer-ownership';
import OwnerOwnership from '@webapp/pages/release/release-track-info/owners-ownership';
import OwnershipPublished from '@webapp/pages/release/release-track-info/ownership-published';
import { getTrackIdFromPath } from '@webapp/utils/tracks/get-track-id-from-url-path';

type InviteArguments = {
  contact: ContactInfo | undefined;
  type: 'collaborator' | 'credits';
  setError: React.Dispatch<React.SetStateAction<React.ReactNode>>;
  newCollabOrCredit: NewCollaborator | undefined;
  name: string;
};

interface ReleaseTrackInfoPageProps {
  className?: string;
  trackExists: boolean;
  nextTrackId?: string;
  releaseId: string;
  releaseStatus?: ReleaseStatus;
  releaseType?: ReleaseType;
  currentTrack?: Partial<SimpleTrack>;
  songName: string;
  isStepValid?: boolean;
  isFullReleaseValid: boolean;
  isSummaryButtonEnabled: boolean;
  trackId: string;
  isValid: boolean;
  isFirstTrack: boolean;
  isLastTrack: boolean;
  videoLoading: boolean;
  invalidForm: boolean;
  tracks: Partial<SimpleTrack>[];
  songOrder?: number;
  isMobile: boolean;
  isTablet: boolean;
  isOwnershipSplitValid: boolean;
  isImported: boolean;
  // isOwnerShipValid: boolean;

  getOwnership: (assetId: string) => GetOwnerships;
  getCollaborators: (assetId: string) => GetCollaborators;
  nextSection: (trackId?: string) => CallHistoryMethodAction;
  updateGlobalInvalid: any;
  goBack: () => CallHistoryMethodAction;

  activeArtist?: SimpleArtist;
  labelPlan: RecordLabelSubscriptionType;
  inviteArtist: (
    invite: InviteArtistRequest,
    source?: ArtistInviteSource
  ) => CreateArtistInvite;
  allowReplicateCollaborators?: boolean;
  disableReplicateCollaborators: () => DisableReplicateCollaborators;
  showIsrcExistsError: (originalReleaseTitle: string) => ShowSnackbar;
}

const mapStateToProps = (state: AppState) => {
  const activeTrackId = getTrackIdFromPath(state);
  const currentTrack = state.tracks.tracks[activeTrackId];
  const tracks = trackList(state.tracks.tracks, state.release.release?.id);
  const isSong = currentTrack?.type === 'song';

  const getNextTrackIndex = () => {
    const currentIndex = tracks.findIndex((t) => t.id === activeTrackId);
    if (
      currentIndex >= 0 &&
      tracks.length > 1 &&
      tracks.length - 1 > currentIndex
    ) {
      return currentIndex + 1;
    } else {
      return undefined;
    }
  };

  const nextTrackIndex = getNextTrackIndex();
  const nextTrackId = nextTrackIndex ? tracks[nextTrackIndex].id : undefined;
  const assetStatus = nextTrackId
    ? state.assets.statuses[nextTrackId]
    : undefined;
  const trackProcessing = !assetStatus?.validationDone;

  const invalidForm = memoize(() => {
    const isExplicit = currentTrack?.id
      ? state.tracks.tracks[currentTrack.id].isExplicit
      : undefined;
    const tracksWithCurrentIsrc = trackList(
      state.tracks.tracks,
      state.release.release?.id
    ).filter((track) => track.isrc === currentTrack?.isrc);
    const conditions = [
      //missingArtists
      !collaboratorsList(state.collaborator, activeTrackId).length,
      //missingMainArtist
      !collaboratorsList(state.collaborator, activeTrackId).find(
        (a) => a.role === 'main'
      ),
      //missingVideo
      currentTrack?.hasMusicVideo && !currentTrack?.video?.videoAssetId,
      //missingPoster
      currentTrack?.hasMusicVideo && !currentTrack?.video?.coverAssetId,
      //missingLanguage
      isSong ? !currentTrack?.language : false,
      //missingType
      currentTrack?.type === undefined,
      //isExplicit
      isExplicit === undefined && isSong,
      // invalidIsrc
      currentTrack?.isrc ? !isISRC(currentTrack?.isrc) : false,
      //repeatedIsrc
      currentTrack?.isrc ? tracksWithCurrentIsrc.length > 1 : false,
      // missingSongName
      !currentTrack?.title,
    ];
    return conditions.some((cond) => cond);
  });

  const ownerShipOwner =
    assetOwnersById(state.ownership.assets, activeTrackId, 'OWNER') ?? [];
  const ownerShipWriter =
    assetOwnersById(state.ownership.assets, activeTrackId, 'WRITER') ?? [];
  const isOwnershipSplitValid =
    ownerShipOwner.reduce((memo, owner) => {
      return memo + owner.split * 10 ** 10;
    }, 0) /
      10 ** 10 ===
      100 &&
    ownerShipWriter.reduce((memo, writer) => {
      return memo + writer.split * 10 ** 10;
    }, 0) /
      10 ** 10 ===
      100;

  return {
    tracks,
    trackId: activeTrackId,
    trackExists: activeTrackId in state.tracks.tracks,
    nextTrackId: trackProcessing ? undefined : nextTrackId,
    releaseId: state.release.release?.id as string,
    releaseType: state.release.release?.type,
    releaseStatus: state.release.release?.status,
    isStepValid: trackSection(state, activeTrackId),
    isFullReleaseValid: fullRelease(state),
    isSummaryButtonEnabled: validStatusForSummaryButton(state),
    isImported: !!state.release.release?.source,
    disableEditing:
      blockEditor(state) || state.release.release?.status === 'taken-down',
    isValid: currentTrack?.status !== 'invalid',
    invalidForm: invalidForm(),
    videoLoading:
      !!currentTrack?.hasMusicVideo &&
      !!currentTrack.video?.videoAssetId &&
      currentTrack.video?.videoAssetId in state.assets.assets &&
      state.assets.assets[currentTrack.video?.videoAssetId]?.status ===
        'uploading',
    songName: currentTrack?.title || '',
    songOrder: currentTrack?.order,
    isFirstTrack: tracks.findIndex((t) => t.id === activeTrackId) === 0,
    isLastTrack: nextTrackIndex === undefined,
    activeArtist: state.artist.active,
    labelPlan: ((state.recordLabels.active?.id &&
      state.subscription.label[
        state.recordLabels.active.id
      ]?.plan.toUpperCase()) ||
      'FREE') as RecordLabelSubscriptionType,
    isMobile: state.ui.mobile,
    isTablet: state.ui.tablet,
    isOwnershipSplitValid,
    allowReplicateCollaborators: !state.tracks.disableReplicateCollaborators,
    currentTrack,
  };
};

const mapDispatchToProps = (dispatch: Dispatch) => ({
  updateGlobalInvalid: (value: boolean) => dispatch(new UpdateInvalid(value)),
  nextSection: (trackId?: string) =>
    dispatch(push(trackId ? `./${trackId}` : '../preview')),
  getOwnership: (assetId: string) => dispatch(new GetOwnerships(assetId)),
  getCollaborators: (assetId: string) =>
    dispatch(new GetCollaborators(assetId)),
  goBack: () => dispatch(push('../title')),
  inviteArtist: (invite: InviteArtistRequest, source?: ArtistInviteSource) =>
    dispatch(new CreateArtistInvite(invite, source)),
  disableReplicateCollaborators: () =>
    dispatch(new DisableReplicateCollaborators()),
  showIsrcExistsError: (originalReleaseTitle: string) =>
    dispatch(
      new ShowSnackbar('RELEASE.ISRC_EXISTS_ERROR.SNACKBAR', {
        title: <Translate id="TRACK.ISRC_EXISTS.SNACKBAR.TITLE" />,
        text: (
          <Translate
            id="TRACK.ISRC_EXISTS.SNACKBAR.TEXT"
            data={{ title: originalReleaseTitle }}
          />
        ),
        hideDismiss: false,
      })
    ),
});

export const ReleaseTrackInfoPage = ({
  invalidForm,
  nextSection,
  nextTrackId,
  trackId,
  currentTrack,
  trackExists,
  className,
  releaseId,
  songName,
  tracks,
  songOrder,
  releaseType,
  releaseStatus,
  isStepValid,
  videoLoading,
  isFirstTrack,
  isLastTrack,
  isFullReleaseValid,
  isSummaryButtonEnabled,
  isValid,
  isImported,
  isOwnershipSplitValid,
  getOwnership,
  getCollaborators,
  activeArtist,
  labelPlan,
  inviteArtist,
  updateGlobalInvalid,
  isMobile,
  isTablet,
  allowReplicateCollaborators,
  disableReplicateCollaborators,
  showIsrcExistsError,
}: ReleaseTrackInfoPageProps) => {
  const [validateErrors, setValidateErrors] = React.useState(0);
  const [validateForm, setValidateForm] = React.useState(false);
  const [isShowErrors, setIsShowErrors] = React.useState(false);
  const [collaborators, setCollaborators] =
    React.useState<UpdateCollaboratorsPlatforms[]>();
  const [collaboratorContact, setCollaboratorContact] =
    React.useState<ContactInfo>();

  const [collaboratorError, setCollaboratorError] =
    React.useState<React.ReactNode>();

  const [newCollaborator, setNewCollaborator] =
    React.useState<NewCollaborator>();

  const [collaboratorName, setCollaboratorName] = React.useState(
    newCollaborator?.name ?? ''
  );

  const [contributorContact, setContributorContact] =
    React.useState<ContactInfo>();

  const [contributorError, setContributorError] =
    React.useState<React.ReactNode>();

  const [newContributor, setNewContributor] = React.useState<NewCollaborator>();

  const [contributorName, setContributorName] = React.useState(
    newCollaborator?.name ?? ''
  );

  const [ISRCLocked, setISRCLocked] = React.useState(false);

  React.useEffect(() => {
    setValidateForm(false);
    getOwnership(trackId);
    getCollaborators(trackId);
  }, [trackId, getOwnership, getCollaborators]);

  React.useEffect(() => {
    scrollOnError();
  }, [validateErrors]);

  React.useEffect(() => {
    if (currentTrack?.isrcExists) {
      showIsrcExistsError(currentTrack.originalReleaseTitle || '');
      setISRCLocked(true);
    } else {
      setISRCLocked(false);
    }
  }, [currentTrack, showIsrcExistsError]);
  const handleNextStepClick = () => {
    disableReplicateCollaborators();

    if (isLastTrack) {
      updateGlobalInvalid(true);
    }
    setValidateForm(true);
    if (isValidForm() && isOwnershipSplitValid) {
      setIsShowErrors(false);
      setValidateForm(false);

      handleCollaboratorsInvite({
        type: 'collaborator',
        contact: collaboratorContact,
        name: collaboratorName,
        newCollabOrCredit: newCollaborator,
        setError: setCollaboratorError,
      });

      handleCollaboratorsInvite({
        type: 'credits',
        contact: contributorContact,
        name: contributorName,
        newCollabOrCredit: newContributor,
        setError: setContributorError,
      });

      nextSection(nextTrackId);
    } else {
      setIsShowErrors(true);
      setValidateErrors(validateErrors + 1);
    }
  };

  const isValidForm = () => {
    const valid = !invalidForm;
    if (valid && !nextTrackId) {
      return isFullReleaseValid;
    }
    return isStepValid && valid;
  };

  const handleCollaboratorsInvite: (
    args: InviteArguments
  ) => Promise<void> = async ({
    contact,
    type,
    setError,
    newCollabOrCredit,
    name,
  }) => {
    if (!contact) {
      return;
    }

    setError(undefined);

    if (!newCollabOrCredit?.role) {
      setError(<Translate id="INVITE.MISSING_ROLE" />);
      return;
    }

    try {
      const inviter = activeArtist;
      if (inviter) {
        const invite: InviteArtistRequest = {
          type,
          contactType: contact.type,
          invitee: contact.value,
          artist: {
            name,
          },
          inviter,
          role: newCollabOrCredit.role,
          songName: songName,
          recordLabel: {
            plan: labelPlan,
          },
          releaseId,
        };
        inviteArtist(invite, {
          type: type === 'collaborator' ? 'COLLABORATOR' : 'CONTRIBUTOR',
          assetType: 'TRACK',
          id: trackId,
        });
      }
    } catch (e) {
      setCollaboratorError(<Translate id={(e as Error).message} />);
    }
  };

  return trackExists ? (
    <MotionOpacityContainer className={className}>
      <TrackLoadingCtrl trackId={trackId} />

      <AnimatePresence exitBeforeEnter>
        {songName && !isMobile && (
          <TrackInfoTitleCtrl className="mb-30" songName={songName} />
        )}
      </AnimatePresence>

      <Wrapper>
        <GeneralTrackInfo validateForm={validateForm} />
        <CollaboratorsCtrl
          id="collaboratorDropdown"
          label={<Translate id="RELEASE.TRACK_INFO.TRACK.COLLABORATOR.TITLE" />}
          text={<Translate id="RELEASE.TRACK_INFO.TRACK.COLLABORATOR.TEXT" />}
          assetId={trackId}
          collaboratorType="COLLABORATOR"
          songName={songName}
          roleList={COLLABORATOR_ROLES}
          requiredRole={releaseType === 'single' ? 'main' : undefined}
          defaultRole="featuring"
          hasTabsSelector
          firstBigger
          validate={validateForm}
          onChange={setCollaborators}
          onRemove={(id) =>
            setCollaborators(
              collaborators?.filter((col) => col.collaboratorId !== id)
            )
          }
          requirePlatformIds
          showError={collaboratorError}
          setShowError={setCollaboratorError}
          handleEmailOrPhoneChange={setCollaboratorContact}
          setNewCollaborator={setNewCollaborator}
          setCollaboratorName={setCollaboratorName}
          canBeWithoutInvite={true}
          locked={ISRCLocked}
          hideEmailInput
        />

        {releaseStatus !== 'published' && (
          <WriterOwnership
            songName={songName}
            trackId={trackId}
            releaseId={releaseId}
            addWriterToOtherTracks={isFirstTrack && allowReplicateCollaborators}
            locked={ISRCLocked}
          />
        )}

        {releaseStatus !== 'published' && (
          <OwnerOwnership
            songName={songName}
            trackId={trackId}
            releaseId={releaseId}
            addOwnerToOtherTracks={isFirstTrack && allowReplicateCollaborators}
            locked={ISRCLocked}
          />
        )}

        {releaseStatus === 'published' && <OwnershipPublished />}

        <CreditsTrackInfo
          trackId={trackId}
          collaborators={collaborators}
          songName={songName}
          isShowErrors={isShowErrors}
          setCollaborators={setCollaborators}
          handleContributorContactChange={setContributorContact}
          contributorError={contributorError}
          setContributorError={setContributorError}
          setNewContributor={setNewContributor}
          setContributorName={setContributorName}
          canBeWithoutInvite={true}
          locked={ISRCLocked}
        />

        <TrackPreviewCtrl trackId={trackId} />
        {!isImported && <CoverCopyrightMaterialCtrl trackId={trackId} />}
      </Wrapper>

      {(isSummaryButtonEnabled || isMobile) && (
        <CounterFixedButton
          className={!isTablet ? '' : 'mt-30'}
          fixed={!isTablet}
          isValid={isValid}
          bottomMargin={
            isFullReleaseValid && isSummaryButtonEnabled && !isLastTrack
              ? '55px'
              : undefined
          }
          currentStep={songOrder}
          maxStep={tracks.length}
          disableLoader={
            isLastTrack ? !videoLoading : !videoLoading && !!nextTrackId
          }
          isLoading={isLastTrack ? videoLoading : videoLoading || !nextTrackId}
          customLabel={
            isLastTrack ? (
              'Summary'
            ) : nextTrackId ? (
              'Next Song'
            ) : (
              <Translate id="RELEASE.PROCESSING" />
            )
          }
          onNext={handleNextStepClick}
        />
      )}
      {isFullReleaseValid && !isLastTrack && (
        <SummaryButton
          className={!isTablet ? '' : 'mt-30'}
          fixed={!isTablet}
          onClick={() => nextSection()}
        />
      )}
    </MotionOpacityContainer>
  ) : null;
};

export default React.memo(
  connect(mapStateToProps, mapDispatchToProps)(ReleaseTrackInfoPage),
  (prevProps: any, nextProps: any) => {
    return isEqual(prevProps.myProp, nextProps.myProp); // deep comparison of myProp object
  }
);

const MotionOpacityContainer = styled(MotionOpacity)`
  margin-bottom: 100px;

  ${({ theme }) => `@media screen and ${theme.device.laptopS}`} {
    margin: 0;
  }
`;

const Wrapper = styled.div<{ gap?: number }>`
  display: grid;
  grid-gap: ${({ gap }) => (gap ? `${gap}px` : '3px')};
`;
